import type { ContractAttachmentView } from "@closer/shared";
import { Button, Card, EmptyState, FileInput, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { uploadFile } from "../../lib/fileUpload";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * SÖZLEŞME EKLERİ (CLOS-121 `[103]`).
 *
 * ### ⚠ Ekler ana belgeden AYRI duruyor
 *
 * Kart: *"ekler ana sözleşme versiyonundan ve versiyon geçmişinden AÇIKÇA AYRILIR."*
 * Bu yüzden kendi bölümü ve kendi dili var: burada "versiyon" ya da "sürüm" kelimesi
 * geçmiyor; bir ek yüklemek sözleşmenin metnini değiştirmiyor.
 *
 * ### ⚠ Yükleme İKİ ADIM ama TEK niyet
 *
 * Dosya önce depolamaya gidiyor (`[005]` presign), sonra ek olarak bağlanıyor. Kullanıcı
 * bunu bilmek zorunda değil: tek düğme, tek sonuç. Yarım kalan yükleme ek üretmiyor.
 *
 * ### ⚠ Düğmelerin görünmesi güvenlik kararı DEĞİL
 *
 * Kart bunu açıkça söylüyor. `can()` yalnız kullanıcıya yapamayacağı bir şeyi teklif
 * etmemek için; sunucu her uçta yetkiyi ve kayıt erişimini yeniden uyguluyor.
 */

/**
 * Boş ek listesinin açıklaması.
 *
 * ⚠ NE OLDUĞUNU ANLATIYOR — canlı turda ölçüldü (4 Eylül). Eski metin ("Bu sözleşmeye
 * henüz bir ek eklenmedi") ekranda zaten görülen şeyi tekrar ediyordu; kullanıcının asıl
 * sorusu "ek nedir, sözleşme metninden farkı ne?" idi ve cevapsız kalıyordu. Ekler ana
 * belgeden ve versiyon geçmişinden AYRI bir kavram — boş durum tam da bunu söylüyor.
 */
const BOS_EK_ACIKLAMASI =
  "Sözleşme metninin dışında kalan belgeler burada durur: imza sirküleri, vekâletname, " +
  "teklif, yazışma. Sözleşmenin kendi metni ve sürümleri ayrı bölümlerde.";

function boyut(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const birimler = ["KB", "MB", "GB"];
  let deger = bytes / 1024;
  let i = 0;
  while (deger >= 1024 && i < birimler.length - 1) {
    deger /= 1024;
    i += 1;
  }
  return `${deger.toFixed(deger < 10 ? 1 : 0)} ${birimler[i]}`;
}

export function ContractAttachments({
  contractId,
  kayitYazma = true,
}: {
  contractId: string;
  /** Görüntüleyenin bu kayıttaki yazma erişimi (CLOS-888). */
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const dosyaSecici = useRef<HTMLInputElement>(null);
  const [baslik, setBaslik] = useState("");
  const [gecmisiGoster, setGecmisiGoster] = useState(false);

  /**
   * KAYIT DÜZEYİ YAZMA (CLOS-888).
   *
   * ⚠ Rol yetkisi tek başına yetmiyor: kayda yalnız `read` erişimi olan üye rol yetkisi
   * taşıyabiliyor (227/227 `standard_user` rolü `contract.edit` taşıyor). Sunucu her
   * zaman fail-closed'du; kapatılan şey yetki açığı değil, ürünün yapamadığını
   * yapabilirmiş gibi göstermesi.
   *
   * ⚠ Varsayılan `true`: alan yoksa yazabilir sayılıyor. Riskin yönü asimetrik —
   * gereksiz gösterilen düğme 404 verir, gereksiz gizlenen düğme işi tamamen durdurur.
   */
  const yazabilir = can("contract.file") && kayitYazma;

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "ekler", String(gecmisiGoster)),
    queryFn: async () =>
      (
        await api<ContractAttachmentView[]>(
          `/api/contracts/${contractId}/attachments${gecmisiGoster ? "?includeRemoved=true" : ""}`,
          { tenantId },
        )
      ).data,
  });

  const yenile = () =>
    queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "contract", contractId, "ekler"),
    });

  const yukle = useMutation({
    mutationFn: async (file: File) => {
      const yuklenen = await uploadFile({
        file,
        domain: "contract",
        entityId: contractId,
        tenantId,
      });
      return api<ContractAttachmentView>(`/api/contracts/${contractId}/attachments`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          storageObjectId: yuklenen.storageObjectId,
          /** Başlık boşsa dosya adı: kullanıcıyı iki alan doldurmaya zorlamıyoruz. */
          title: baslik.trim() || yuklenen.fileName,
        }),
      });
    },
    onSuccess: async () => {
      setBaslik("");
      if (dosyaSecici.current) dosyaSecici.current.value = "";
      await yenile();
      toast.success("Ek yüklendi");
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Ek yüklenemedi"),
  });

  const kaldir = useMutation({
    mutationFn: (id: string) =>
      api(`/api/contracts/${contractId}/attachments/${id}`, { method: "DELETE", tenantId }),
    onSuccess: async () => {
      await yenile();
      toast.success("Ek kaldırıldı");
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Ek kaldırılamadı"),
  });

  /**
   * ⚠ İndirme adresi TIKLANDIĞI AN isteniyor (`[101]` kuralı): imzalı adres DOM'da
   * durmuyor ve her indirmede erişim kararı yeniden veriliyor.
   */
  const indir = useMutation({
    mutationFn: async (id: string) =>
      (
        await api<{ downloadUrl: string; fileName: string | null }>(
          `/api/contracts/${contractId}/attachments/${id}/download-url`,
          { tenantId },
        )
      ).data,
    onSuccess: (bilet) => {
      const bag = document.createElement("a");
      bag.href = bilet.downloadUrl;
      bag.rel = "noopener noreferrer";
      if (bilet.fileName) bag.download = bilet.fileName;
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Ek indirilemedi"),
  });

  return (
    <Card
      title="Ekler"
      actions={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setGecmisiGoster((ö) => !ö)}
        >
          {gecmisiGoster ? "Yalnız güncel ekler" : "Kaldırılanları da göster"}
        </Button>
      }
    >
      {sorgu.isPending ? (
        <div role="status" aria-label="Ekler yükleniyor">
          <Skeleton lines={2} />
        </div>
      ) : sorgu.error || !sorgu.data ? (
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            overrides: {
              forbiddenOrMissing: {
                title: "Ekler görüntülenemiyor",
                description: "Bu sözleşmeye erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : sorgu.data.length === 0 ? (
        <EmptyState title="Ek yok" description={BOS_EK_ACIKLAMASI} />
      ) : (
        <ul className="flex flex-col gap-2">
          {sorgu.data.map((ek) => (
            <li key={ek.id} className="flex flex-wrap items-baseline gap-2 text-sm">
              <span
                className={`font-medium ${ek.removedAt ? "text-fg-faint line-through" : "text-fg-strong"}`}
              >
                {ek.title}
              </span>
              <span className="text-xs text-fg-soft">
                {ek.fileName} · {boyut(ek.sizeBytes)} · {ek.uploadedByName}
              </span>
              {/* ⚠ Kaldırılmış ek GEÇMİŞ olarak duruyor; indirilemiyor, aksiyon da sunulmuyor. */}
              {ek.removedAt ? (
                <span className="text-xs text-fg-faint">
                  {new Date(ek.removedAt).toLocaleDateString("tr-TR")} tarihinde kaldırdı:{" "}
                  {ek.removedByName ?? "Bilinmiyor"}
                </span>
              ) : (
                <span className="ml-auto flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => indir.mutate(ek.id)}
                    aria-label={`${ek.title} ekini indir`}
                  >
                    İndir
                  </Button>
                  {yazabilir && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={kaldir.isPending}
                      onClick={() => kaldir.mutate(ek.id)}
                      aria-label={`${ek.title} ekini kaldır`}
                    >
                      Kaldır
                    </Button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {yazabilir && (
        <div className="mt-4 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-fg-soft">Ek başlığı</span>
            <Input
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              placeholder="Örn. İmza sirküleri"
            />
          </label>
          <FileInput
            ref={dosyaSecici}
            aria-label="Ek dosyası seç"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) yukle.mutate(file);
            }}
            disabled={yukle.isPending}
          />
          {yukle.isPending && <span className="text-xs text-fg-soft">Yükleniyor…</span>}
        </div>
      )}
    </Card>
  );
}
